import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useNavigationPage from '../../hooks/useNavigationPage';

import usePackingTypeService from '../../app/services/hooks/usePackingTypeService';
import { PackingType, PackingTypeFilters } from '../../app/models/PackingType';
import PackingTypeDataTable from './components/tables/PackingTypeDataTable';
import PackingTypeCreate from './components/PackingTypeCreate';
import PackingTypeEdit from './components/PackingTypeEdit';

const breadcrumbs: BreadcrumbsItem[] = [
  {
    name: 'Tipos de empaque',
    url: '/packing-types',
    isActive: true
  }
];

const PackingTypes: React.FC = () => {
  const { navigationPage } = useNavigationPage();
  const { showLoading, hideLoading } = useContext(AppContext);
  const { fetchingGetPackingTypes, getPackingTypes, deletePackingType } = usePackingTypeService();

  const [packingTypes, setPackingTypes] = useState<PackingType[]>([]);
  const [packingTypeIdEdit, setPackingTypeIdEdit] = useState<number>(-1);
  const [showingEdit, setShowingEdit] = useState(false);
  const [showingCreate, setShowingCreate] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [filter, setFilter] = useState<PackingTypeFilters>({
    page: 1,
    per_page: 10,
    sort: 'id',
    order: 'asc'
  });

  useEffect(() => {
    reloadTable();
  }, [filter]);

  const getAllPackingTypes = () => {
    getPackingTypes(filter, {
      onSuccess: (response: ServiceResponse) => {
        setPackingTypes(response.data.packing_types);
        setTotalRows(response.data.total_rows);
      },
      onError: (response: ServiceResponse) => {
        toast.error(response.message);
        if (response.data.to_dashboard) {
          navigationPage('/');
        }
      }
    });
  };

  const reloadTable = () => {
    getAllPackingTypes();
  };

  const showCreate = () => setShowingCreate(true);
  const hideCreate = () => setShowingCreate(false);

  const showEdit = (id: number) => {
    setShowingEdit(true);
    setPackingTypeIdEdit(id);
  };
  const hideEdit = () => {
    setShowingEdit(false);
    setPackingTypeIdEdit(-1);
  };

  const destroy = (id: number) => {
    useReactConfirmAlert().requestConfirmation({
      title: '¿Estás seguro?',
      message: `Está a punto de eliminar el tipo de empaque #${id}`,
      buttons: {
        confirmButton: {
          label: 'Si, confirmar',
          onClick: () => {
            if (showLoading) showLoading('loading', 'Eliminando tipo de empaque...');
            deletePackingType(id, {
              onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().successAlert({
                  title: 'Éxito',
                  message: response.message
                });
                reloadTable();
              },
              onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                  title: 'Error',
                  message: response.message
                });
              }
            });
          }
        },
        cancelButton: {
          label: 'No, cancelar',
          onClick: () => {
            setTimeout(() => {
              useReactConfirmAlert().infoAlert({
                title: 'Cancelado',
                message: 'El tipo de empaque no se ha eliminado.'
              });
            }, 0);
          }
        }
      }
    });
  };

  return (
    <>
      <Breadcrumbs pageSection="Tipos de empaque" breadcrumbs={breadcrumbs} />

      <DefaultCard>
        <DefaultToolBar
          left={<ButtonCreate callbackCreate={showCreate} title="Nuevo tipo de empaque" />}
        />
        <PackingTypeDataTable
          packingTypes={packingTypes}
          edit={showEdit}
          destroy={destroy}
          loading={fetchingGetPackingTypes}
          filter={filter}
          setFilter={setFilter}
          totalRows={totalRows}
        //   callbackSuccess={reloadTable}
        />
      </DefaultCard>

      {showingCreate && (
        <DefaultModal
          show={showingCreate}
          handleClose={hideCreate}
          title="Crear tipo de empaque"
          backdrop
          showFooter={false}
        >
          <PackingTypeCreate
            onSaved={() => { reloadTable(); hideCreate(); }}
            onCancel={hideCreate}
            onError={hideCreate}
          />
        </DefaultModal>
      )}

      {showingEdit && (
        <DefaultModal
          show={showingEdit}
          handleClose={hideEdit}
          title="Editar tipo de empaque"
          backdrop
          showFooter={false}
        >
          <PackingTypeEdit
            packingTypeId={packingTypeIdEdit}
            onSaved={() => { reloadTable(); hideEdit(); }}
            onCancel={hideEdit}
            onError={hideEdit}
          />
        </DefaultModal>
      )}
    </>
  );
};

export default PackingTypes;
